/**
 * MailboxesPanel.tsx
 *
 * Phase 2 #7 — customer-facing mail management for a hosting account.
 *
 * Three stacked sections:
 *   1. Mail Domains — list of customer's mail domains with verification
 *      status pills (DKIM / SPF / DMARC / MX verified via public DNS).
 *      Add-domain form.
 *   2. Mailboxes — per-selected-domain mailbox table with create/edit/delete.
 *      Edit modal handles password reset, quota change, autoresponder.
 *   3. Aliases & catch-all — per-domain alias table + single catch-all setter.
 *
 * The API calls all go through /api/hosting/accounts/:id/mail/* — auth +
 * ownership is enforced in the backend.
 */
import React, { useEffect, useState } from 'react';

interface MailDomainRow {
  id: string;
  customer_id: string;
  hosting_account_id: string;
  domain: string;
  mail_ct_domain_id: number | null;
  ses_identity_status: 'pending' | 'verifying' | 'verified' | 'failed';
  ses_verified_at: string | null;
  dkim_published_at: string | null;
  mx_published_at: string | null;
  spf_published_at: string | null;
  dmarc_published_at: string | null;
  webmail_a_published_at: string | null;
  webmail_cert_issued_at: string | null;
  webmail_vhost_installed_at: string | null;
  last_error: string | null;
  created_at: string;
}

interface Mailbox {
  id: number;
  domain_id: number;
  domain: string;
  email: string;
  active: boolean;
  quota_mb: number;
  autoresponder_enabled: boolean;
  autoresponder_subject: string | null;
  autoresponder_body: string | null;
  created_at: string;
}

interface MailAlias {
  id: number;
  domain_id: number;
  domain: string;
  source: string;
  destination: string;
  active: boolean;
  is_catchall: boolean;
  created_at: string;
}

const apiBase = process.env.REACT_APP_API_URL || '';

function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('token');
  return fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      'Authorization': `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
}

// ============================================================================
// Top-level panel
// ============================================================================

const MailboxesPanel: React.FC<{ hostingAccountId: string; primaryDomain: string | null }> = ({
  hostingAccountId,
  primaryDomain,
}) => {
  const [domains, setDomains] = useState<MailDomainRow[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [addDomainOpen, setAddDomainOpen] = useState(false);

  const loadDomains = async () => {
    setLoading(true);
    setErr('');
    try {
      const resp = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail`);
      const json = await resp.json();
      if (json.success) {
        setDomains(json.domains || []);
        if (!selectedDomainId && json.domains && json.domains.length > 0) {
          setSelectedDomainId(json.domains[0].id);
        }
      } else {
        setErr(json.error || 'Failed to load mail domains');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDomains(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const selected = domains.find(d => d.id === selectedDomainId) || null;

  return (
    <div className="space-y-6">
      {/* Domains section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="font-semibold text-gray-900">Mail Domains</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Domains you can send + receive email for. Each gets DKIM, SPF, DMARC, and MX records
              published automatically.
            </p>
          </div>
          <button
            onClick={() => setAddDomainOpen(o => !o)}
            className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700"
          >
            {addDomainOpen ? 'Cancel' : 'Add Domain'}
          </button>
        </div>

        {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

        {addDomainOpen && (
          <AddDomainForm
            hostingAccountId={hostingAccountId}
            defaultDomain={primaryDomain || ''}
            onDone={async () => { setAddDomainOpen(false); await loadDomains(); }}
            onError={setErr}
          />
        )}

        {loading && domains.length === 0 ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : domains.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">
            No mail domains yet. Click <strong>Add Domain</strong> to set one up.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Domain</th>
                <th className="py-2 pr-3">DKIM</th>
                <th className="py-2 pr-3">SPF</th>
                <th className="py-2 pr-3">DMARC</th>
                <th className="py-2 pr-3">MX</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {domains.map(d => {
                const incomplete = !d.mail_ct_domain_id || !d.dkim_published_at ||
                  !d.spf_published_at || !d.dmarc_published_at || !d.mx_published_at ||
                  d.ses_identity_status === 'pending';
                return (
                  <tr
                    key={d.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedDomainId === d.id ? 'bg-teal-50' : ''}`}
                    onClick={() => setSelectedDomainId(d.id)}
                  >
                    <td className="py-2 pr-3 font-medium text-gray-900">{d.domain}</td>
                    <td className="py-2 pr-3"><CheckPill done={!!d.dkim_published_at} /></td>
                    <td className="py-2 pr-3"><CheckPill done={!!d.spf_published_at} /></td>
                    <td className="py-2 pr-3"><CheckPill done={!!d.dmarc_published_at} /></td>
                    <td className="py-2 pr-3"><CheckPill done={!!d.mx_published_at} /></td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      <RefreshButton
                        hostingAccountId={hostingAccountId}
                        cmdId={d.id}
                        onDone={loadDomains}
                        incomplete={incomplete}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {selected?.last_error && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            <strong>Last error on {selected.domain}:</strong> {selected.last_error}
          </div>
        )}
      </div>

      {/* DNS records to add — MX / SPF / DKIM / DMARC values for the
          selected mail domain, so the customer knows exactly what to point
          at their DNS provider. */}
      {selected && (
        <MailDnsRecords hostingAccountId={hostingAccountId} domain={selected.domain} />
      )}

      {/* Mailboxes section */}
      {selected && (
        <MailboxList
          hostingAccountId={hostingAccountId}
          domain={selected}
          onError={setErr}
        />
      )}

      {/* Aliases + catch-all */}
      {selected && (
        <AliasList
          hostingAccountId={hostingAccountId}
          domain={selected}
          onError={setErr}
        />
      )}

      {/* Webmail + IMAP info */}
      {selected && (
        <ServerInfoCard
          mailHost="mail.12and22.com"
          webmailHost={selected.webmail_vhost_installed_at ? `webmail.${selected.domain}` : null}
          webmailFallback="webmail.12and22.com"
        />
      )}
    </div>
  );
};

// ============================================================================
// Add-domain form
// ============================================================================

const AddDomainForm: React.FC<{
  hostingAccountId: string;
  defaultDomain: string;
  onDone: () => void | Promise<void>;
  onError: (m: string) => void;
}> = ({ hostingAccountId, defaultDomain, onDone, onError }) => {
  const [domain, setDomain] = useState(defaultDomain);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) { onError('Domain is required'); return; }
    setBusy(true);
    try {
      const resp = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains`, {
        method: 'POST',
        body: JSON.stringify({ domain: trimmed }),
      });
      const json = await resp.json();
      if (json.success) {
        setDomain('');
        await onDone();
      } else {
        onError(json.error || 'Add failed');
      }
    } catch (e: any) {
      onError(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200">
      <label className="block text-xs text-gray-600 mb-1">Domain (e.g. example.com)</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={domain}
          onChange={e => setDomain(e.target.value)}
          placeholder="example.com"
          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
          disabled={busy}
        />
        <button
          onClick={submit}
          disabled={busy}
          className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 disabled:opacity-50"
        >
          {busy ? 'Provisioning…' : 'Add'}
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Setup takes ~30 seconds. We&apos;ll then show the DNS records (MX, SPF, DKIM, DMARC) to add
        at your DNS provider, and Verify checks when they&apos;re live.
      </p>
    </div>
  );
};

// ============================================================================
// Pills
// ============================================================================

// ============================================================================
// Mail DNS records — the actual MX / SPF / DKIM / DMARC values the customer
// must add at their DNS provider. Pulls from the hosting DNS-records endpoint
// (read-only; 12and22 never edits DNS) and shows the Email group.
// ============================================================================

interface MailRecord { type: string; name: string; value: string; ttl: number; purpose: string; required: boolean }

const MailDnsRecords: React.FC<{ hostingAccountId: string; domain: string }> = ({ hostingAccountId, domain }) => {
  const [records, setRecords] = useState<MailRecord[] | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      setErr(''); setRecords(null);
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/dns-records`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await resp.json();
        if (dead) return;
        if (json.success) {
          const email = (json.data.groups || []).find((g: any) => g.title === 'Email');
          setRecords(email ? email.records : []);
          setNotes(json.data.notes || []);
        } else setErr(json.error || 'Failed to load records');
      } catch (e: any) { if (!dead) setErr(e.message || 'Network error'); }
    })();
    return () => { dead = true; };
  }, [hostingAccountId, domain]);

  const copyVal = async (k: string, v: string) => {
    try { await navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(null), 1500); } catch { /* */ }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 mt-4">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <div>
          <h3 className="font-semibold text-gray-900">Email DNS records for {domain}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Add these at your DNS provider so mail can send, receive and pass spam checks.</p>
        </div>
        <span className="text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-4">
          {err && <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700 mb-3">{err}</div>}
          {!records && !err && <div className="text-sm text-gray-500">Loading records…</div>}
          {records && records.length === 0 && (
            <div className="text-sm text-gray-500">Records appear after mail setup runs for this domain (use “Verify now” above).</div>
          )}
          {records && records.length > 0 && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Name / Host</th>
                    <th className="px-3 py-2">Value</th>
                    <th className="px-3 py-2">TTL</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-gray-900 whitespace-nowrap">{r.type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800 break-all max-w-[160px]">{r.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800 break-all max-w-[300px]">
                        {r.value}
                        <div className="text-[10px] text-gray-400 font-sans mt-0.5">{r.purpose}{r.required ? '' : ' (optional)'}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.ttl}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => copyVal(String(i), r.value)} className="text-xs text-teal-600 hover:text-teal-800 whitespace-nowrap">
                          {copied === String(i) ? 'Copied ✓' : 'Copy'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {notes.length > 0 && (
            <ul className="text-xs text-gray-500 space-y-1 mt-3 list-disc pl-4">
              {notes.filter(n => !/website|A @|A www/i.test(n)).map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const CheckPill: React.FC<{ done: boolean }> = ({ done }) => (
  <span className={`text-xs px-2 py-0.5 rounded ${done ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
    {done ? '✓' : '—'}
  </span>
);

const RefreshButton: React.FC<{
  hostingAccountId: string;
  cmdId: string;
  onDone: () => Promise<void>;
  incomplete?: boolean;
}> = ({ hostingAccountId, cmdId, onDone }) => {
  const [busy, setBusy] = useState(false);
  // Single "Verify now" action: re-checks the domain's mail DNS from public
  // resolvers and advances the provisioning state machine. Replaces the old
  // dual "Continue setup" / "Refresh status" buttons — verification is the
  // only thing the customer needs after adding the records.
  const click = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      await authFetch(
        `/api/hosting/accounts/${hostingAccountId}/mail/domains/${cmdId}/refresh-status`,
        { method: 'POST', body: JSON.stringify({}) },
      );
    } catch (err) {
      console.error('[Mailboxes] verify failed', err);
    } finally {
      setBusy(false);
      await onDone();
    }
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      className="text-xs font-medium bg-slate-900 text-white px-3 py-1 rounded hover:bg-slate-800 disabled:opacity-60"
      title="Re-check this domain's mail DNS records now"
    >
      {busy ? 'Verifying…' : 'Verify now'}
    </button>
  );
};

// ============================================================================
// Mailbox list (for selected domain)
// ============================================================================

const MailboxList: React.FC<{
  hostingAccountId: string;
  domain: MailDomainRow;
  onError: (m: string) => void;
}> = ({ hostingAccountId, domain, onError }) => {
  const [list, setList] = useState<Mailbox[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Mailbox | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/mailboxes`);
      const j = await r.json();
      if (j.success) setList(j.mailboxes || []);
      else onError(j.error || 'Failed to load mailboxes');
    } catch (e: any) {
      onError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [domain.id]);

  const remove = async (m: Mailbox) => {
    if (!window.confirm(`Delete mailbox ${m.email}? All messages will be permanently lost.`)) return;
    try {
      const r = await authFetch(
        `/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/mailboxes/${m.id}`,
        { method: 'DELETE' },
      );
      const j = await r.json();
      if (j.success) await load(); else onError(j.error || 'Delete failed');
    } catch (e: any) { onError(e.message || 'Network error'); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">Mailboxes for {domain.domain}</h2>
          <p className="text-xs text-gray-500 mt-0.5">Each mailbox can sign in via IMAP, POP3, or webmail.</p>
        </div>
        <button
          onClick={() => setShowCreate(s => !s)}
          className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700"
          disabled={!domain.mail_ct_domain_id}
        >
          {showCreate ? 'Cancel' : 'Create Mailbox'}
        </button>
      </div>

      {!domain.mail_ct_domain_id && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800">
          Mail server is still provisioning this domain. Verify now above; mailboxes can be created
          once the row is ready.
        </div>
      )}

      {showCreate && (
        <CreateMailboxForm
          hostingAccountId={hostingAccountId}
          domain={domain}
          onDone={async () => { setShowCreate(false); await load(); }}
          onError={onError}
        />
      )}

      {loading && list.length === 0 ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-gray-500 py-4">
          No mailboxes yet. Create one with the button above.
        </div>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Quota</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Autoresponder</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map(m => (
              <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 pr-3 font-medium text-gray-900">{m.email}</td>
                <td className="py-2 pr-3 text-gray-700">{m.quota_mb} MB</td>
                <td className="py-2 pr-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${m.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {m.active ? 'active' : 'disabled'}
                  </span>
                </td>
                <td className="py-2 pr-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${m.autoresponder_enabled ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                    {m.autoresponder_enabled ? 'on' : 'off'}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right space-x-3 whitespace-nowrap">
                  <a
                    // Per-domain webmail (webmail.<domain>) only exists when a
                    // vhost was provisioned for it; migrated/manual-DNS domains
                    // don't have one, so use the shared webmail. Prevents the
                    // 404 on webmail.<domain>.
                    href={`https://${domain.webmail_vhost_installed_at ? `webmail.${m.domain}` : 'webmail.12and22.com'}/?_user=${encodeURIComponent(m.email)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-teal-700 hover:underline inline-flex items-center"
                    title={`Open webmail for ${m.email}`}
                  >
                    Webmail ↗
                  </a>
                  <button onClick={() => setEditing(m)} className="text-xs text-teal-700 hover:underline">Edit</button>
                  <button onClick={() => remove(m)} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditMailboxModal
          hostingAccountId={hostingAccountId}
          domain={domain}
          mailbox={editing}
          onClose={() => setEditing(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
};

const CreateMailboxForm: React.FC<{
  hostingAccountId: string;
  domain: MailDomainRow;
  onDone: () => Promise<void>;
  onError: (m: string) => void;
}> = ({ hostingAccountId, domain, onDone, onError }) => {
  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  const [quotaMb, setQuotaMb] = useState(1024);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!localPart.trim() || !password) { onError('Email name and password are required'); return; }
    if (password.length < 8) { onError('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/mailboxes`, {
        method: 'POST',
        body: JSON.stringify({ localPart: localPart.trim().toLowerCase(), password, quotaMb }),
      });
      const j = await r.json();
      if (j.success) {
        setLocalPart(''); setPassword(''); setQuotaMb(1024);
        await onDone();
      } else {
        onError(j.error || 'Create failed');
      }
    } catch (e: any) { onError(e.message || 'Network error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-3">
      <div className="md:col-span-2">
        <label className="block text-xs text-gray-600 mb-1">Name (the part before @)</label>
        <div className="flex items-center">
          <input
            type="text"
            value={localPart}
            onChange={e => {
              // Accept a pasted full address gracefully: keep only the part
              // before '@', lowercase, and drop characters not valid in a
              // local-part so the field can never build an invalid value.
              const cleaned = e.target.value
                .split('@')[0]
                .toLowerCase()
                .replace(/[^a-z0-9._%+-]/g, '');
              setLocalPart(cleaned);
            }}
            placeholder="alice"
            className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
            disabled={busy}
          />
          <span className="text-sm text-gray-600 ml-1 whitespace-nowrap">@{domain.domain}</span>
        </div>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Enter only the name — e.g. <span className="font-mono">info</span> makes <span className="font-mono">info@{domain.domain}</span>.
        </p>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Password (min 8)</label>
        <input
          type="text"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="generated or chosen"
          className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
          disabled={busy}
        />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Quota (MB)</label>
        <input
          type="number"
          value={quotaMb}
          min={64}
          max={102400}
          onChange={e => setQuotaMb(Math.max(64, Math.min(102400, Number(e.target.value) || 1024)))}
          className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
          disabled={busy}
        />
      </div>
      <div className="md:col-span-4 flex justify-end">
        <button
          onClick={submit}
          disabled={busy}
          className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
};

const EditMailboxModal: React.FC<{
  hostingAccountId: string;
  domain: MailDomainRow;
  mailbox: Mailbox;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}> = ({ hostingAccountId, domain, mailbox, onClose, onRefresh }) => {
  const [password, setPassword] = useState('');
  const [quotaMb, setQuotaMb] = useState(mailbox.quota_mb);
  const [active, setActive] = useState(mailbox.active);
  const [arEnabled, setArEnabled] = useState(mailbox.autoresponder_enabled);
  const [arSubject, setArSubject] = useState(mailbox.autoresponder_subject || '');
  const [arBody, setArBody] = useState(mailbox.autoresponder_body || '');
  const [busy, setBusy] = useState(false);
  // Feedback rendered INSIDE the modal — the top-level panel's error banner
  // sits behind this fixed z-50 overlay, so it was invisible before.
  const [localErr, setLocalErr] = useState('');
  const [msg, setMsg] = useState('');

  const save = async () => {
    setBusy(true);
    setLocalErr('');
    setMsg('');
    const body: any = { quotaMb, active };
    if (password) body.password = password;
    body.autoresponder = { enabled: arEnabled, subject: arSubject, body: arBody };
    try {
      const r = await authFetch(
        `/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/mailboxes/${mailbox.id}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
      const j = await r.json();
      if (j.success) {
        // Show a clear confirmation in the modal, refresh the underlying list
        // so the row reflects the change, then auto-close after a moment.
        setPassword('');
        setMsg(
          password
            ? 'Saved. Password updated and changes applied.'
            : 'Saved. Changes applied.',
        );
        await onRefresh();
        setTimeout(() => { onClose(); }, 1200);
      } else {
        setLocalErr(j.error || 'Update failed');
      }
    } catch (e: any) {
      setLocalErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-semibold text-gray-900">Edit {mailbox.email}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">×</button>
        </div>

        {msg && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded p-2 text-sm text-green-800 flex items-center gap-2">
            <span>✓</span> {msg}
          </div>
        )}
        {localErr && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
            {localErr}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">New password (leave blank to keep)</label>
            <input
              type="text" value={password} onChange={e => setPassword(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-full" disabled={busy}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quota (MB)</label>
            <input
              type="number" value={quotaMb} min={64} max={102400}
              onChange={e => setQuotaMb(Math.max(64, Math.min(102400, Number(e.target.value) || 1024)))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-full" disabled={busy}
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="inline-flex items-center text-sm">
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="mr-2" disabled={busy} />
            Mailbox active (deliveries + IMAP login enabled)
          </label>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4">
          <label className="inline-flex items-center text-sm font-semibold">
            <input type="checkbox" checked={arEnabled} onChange={e => setArEnabled(e.target.checked)} className="mr-2" disabled={busy} />
            Auto-responder
          </label>
          <p className="text-xs text-gray-500 mt-1">
            When on, senders get one automatic reply (at most once per sender per day) while it stays enabled.
          </p>
          <div className="mt-2 space-y-2">
            <input
              type="text" value={arSubject} onChange={e => setArSubject(e.target.value)}
              placeholder="Out of office until Friday"
              className="border border-gray-300 rounded px-2 py-1 text-sm w-full" disabled={busy || !arEnabled}
            />
            <textarea
              value={arBody} onChange={e => setArBody(e.target.value)}
              rows={4}
              placeholder="I'm currently away. I'll respond to your message when I'm back."
              className="border border-gray-300 rounded px-2 py-1 text-sm w-full" disabled={busy || !arEnabled}
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50" disabled={busy}>Cancel</button>
          <button onClick={save} disabled={busy}
                  className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Aliases + catch-all
// ============================================================================

const AliasList: React.FC<{
  hostingAccountId: string;
  domain: MailDomainRow;
  onError: (m: string) => void;
}> = ({ hostingAccountId, domain, onError }) => {
  const [list, setList] = useState<MailAlias[]>([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [catchallDest, setCatchallDest] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/aliases`);
      const j = await r.json();
      if (j.success) {
        setList(j.aliases || []);
        const cat = (j.aliases || []).find((a: MailAlias) => a.is_catchall);
        setCatchallDest(cat ? cat.destination : '');
      } else onError(j.error || 'Failed to load aliases');
    } catch (e: any) { onError(e.message || 'Network error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [domain.id]);

  const createAlias = async () => {
    if (!source.trim() || !destination.trim()) { onError('Source and destination required'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/aliases`, {
        method: 'POST',
        body: JSON.stringify({ source: source.trim(), destination: destination.trim() }),
      });
      const j = await r.json();
      if (j.success) { setSource(''); setDestination(''); await load(); }
      else onError(j.error || 'Create failed');
    } catch (e: any) { onError(e.message || 'Network error'); }
    finally { setBusy(false); }
  };

  const remove = async (a: MailAlias) => {
    if (!window.confirm(`Delete alias ${a.source} → ${a.destination}?`)) return;
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/aliases/${a.id}`, { method: 'DELETE' });
      const j = await r.json();
      if (j.success) await load(); else onError(j.error || 'Delete failed');
    } catch (e: any) { onError(e.message || 'Network error'); }
  };

  const saveCatchall = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`/api/hosting/accounts/${hostingAccountId}/mail/domains/${domain.id}/catchall`, {
        method: 'POST', body: JSON.stringify({ destination: catchallDest.trim() || null }),
      });
      const j = await r.json();
      if (j.success) await load(); else onError(j.error || 'Update failed');
    } catch (e: any) { onError(e.message || 'Network error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h2 className="font-semibold text-gray-900 mb-1">Aliases &amp; Catch-all for {domain.domain}</h2>
      <p className="text-xs text-gray-500 mb-4">
        Aliases forward mail from one address to another. The catch-all captures anything that doesn&apos;t match a real mailbox or alias.
      </p>

      <div className="mb-5 p-4 bg-gray-50 rounded border border-gray-200">
        <label className="block text-xs text-gray-600 mb-1">Catch-all destination (@{domain.domain})</label>
        <div className="flex gap-2">
          <input
            type="email"
            value={catchallDest}
            onChange={e => setCatchallDest(e.target.value)}
            placeholder="leave blank to disable"
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
            disabled={busy || !domain.mail_ct_domain_id}
          />
          <button onClick={saveCatchall} disabled={busy || !domain.mail_ct_domain_id}
                  className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 disabled:opacity-50">
            Save
          </button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="md:col-span-2 flex items-center">
          <input type="text" value={source} onChange={e => setSource(e.target.value)} placeholder="info"
                 className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" disabled={busy || !domain.mail_ct_domain_id} />
          <span className="text-sm text-gray-600 ml-1">@{domain.domain}</span>
        </div>
        <div className="md:col-span-2">
          <input type="email" value={destination} onChange={e => setDestination(e.target.value)}
                 placeholder="forward-to@example.com"
                 className="border border-gray-300 rounded px-2 py-1 text-sm w-full" disabled={busy || !domain.mail_ct_domain_id} />
        </div>
        <button onClick={createAlias} disabled={busy || !domain.mail_ct_domain_id}
                className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 disabled:opacity-50">
          Add Alias
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : list.filter(a => !a.is_catchall).length === 0 ? (
        <div className="text-sm text-gray-500 py-3">No aliases yet.</div>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-3">From</th>
              <th className="py-2 pr-3">Forwards to</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.filter(a => !a.is_catchall).map(a => (
              <tr key={a.id} className="border-b border-gray-100">
                <td className="py-2 pr-3 text-gray-900">{a.source}</td>
                <td className="py-2 pr-3 text-gray-700">{a.destination}</td>
                <td className="py-2 pr-3 text-right">
                  <button onClick={() => remove(a)} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ============================================================================
// Server connection info
// ============================================================================

const ServerInfoCard: React.FC<{
  mailHost: string;
  webmailHost: string | null;
  webmailFallback: string;
}> = ({ mailHost, webmailHost, webmailFallback }) => {
  const [open, setOpen] = useState(false);
  const effectiveWebmail = webmailHost || webmailFallback;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h2 className="font-semibold text-gray-900">Webmail</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Read and send mail from any browser at <code>https://{effectiveWebmail}</code>.
            Each mailbox above has a direct &quot;Webmail&quot; link.
          </p>
          {!webmailHost && (
            <p className="text-xs text-amber-600 mt-1">
              Branded webmail (webmail.<i>your-domain</i>) is being provisioned. The fallback URL works in the meantime.
            </p>
          )}
        </div>
        <a
          href={`https://${effectiveWebmail}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 whitespace-nowrap"
        >
          Open Webmail ↗
        </a>
      </div>
      <button onClick={() => setOpen(o => !o)} className="w-full text-left flex justify-between items-center border-t border-gray-200 pt-3">
        <span className="font-medium text-gray-900 text-sm">Server connection settings (IMAP / SMTP)</span>
        <span className="text-xs text-gray-500">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="mt-3 text-sm grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded p-3 border border-gray-200">
            <div className="text-xs uppercase text-gray-500 mb-1">Incoming (IMAP)</div>
            <div>Host: <code>{mailHost}</code></div>
            <div>Port: <code>993</code> (SSL/TLS)</div>
            <div>Username: full email address</div>
            <div>Password: mailbox password</div>
          </div>
          <div className="bg-gray-50 rounded p-3 border border-gray-200">
            <div className="text-xs uppercase text-gray-500 mb-1">Outgoing (SMTP)</div>
            <div>Host: <code>{mailHost}</code></div>
            <div>Port: <code>587</code> (STARTTLS) or <code>465</code> (SSL)</div>
            <div>Username: full email address</div>
            <div>Password: mailbox password</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailboxesPanel;
